import React from 'react';

import {
    IconCheck,
    IconCircleMinus,
    IconCirclePlus,
    IconDeviceFloppy,
    IconEdit,
    IconFile,
    IconRefresh,
    IconTarget,
    IconUserCheck,
    IconUserStar,
    IconX,
} from '@tabler/icons-react';
import { trimLeadingZeros } from './skuUtils';

const ACTIVITY_COLOR_MAP: Record<string, string> = {
    CHAMPION_CHANGED: 'grape',
    SURVEY_GENERATED: 'gray',
    SURVEY_STARTED: 'yellow',
    COMPETITOR_ADDED: 'grape',
    COMPETITOR_REMOVED: 'red',
    PRICE_ENTRY: 'orange',
    PROOF_UPLOADED: 'blue',
    SURVEY_SUBMITTED_TO_DM: 'green',
    SURVEY_COMPLETED: 'green',
    SURVEY_PROGRESS_SAVED: 'blue',
    SURVEY_CANCELLED: 'red',
};

const ACTIVITY_LABEL_MAP: Record<string, string> = {
    CHAMPION_CHANGED: 'Champion Changed',
    SURVEY_GENERATED: 'Survey Generated',
    SURVEY_STARTED: 'Survey Started',
    COMPETITOR_ADDED: 'Competitor Added',
    COMPETITOR_REMOVED: 'Competitor Removed',
    PRICE_ENTRY: 'Price Entry',
    PROOF_UPLOADED: 'Uploaded Proof',
    SURVEY_SUBMITTED_TO_DM: 'Survey Submitted to DM',
    SURVEY_COMPLETED: 'Survey Completed',
    SURVEY_PROGRESS_SAVED: 'Progress Saved',
    SURVEY_CANCELLED: 'Survey Cancelled',
};

const DEFAULT_COLOR = 'gray';
const DEFAULT_LABEL = 'Activity';

export function getSurveyActivityColor(type: string): string {
    return ACTIVITY_COLOR_MAP[type] ?? DEFAULT_COLOR;
}

export function getSurveyActivityLabel(type: string): string {
    return ACTIVITY_LABEL_MAP[type] ?? DEFAULT_LABEL;
}

export function getSurveyActivityIcon(type: string): React.ReactNode {
    switch (type) {
        case 'SURVEY_GENERATED':
            return <IconRefresh size={16} />;
        case 'SURVEY_STARTED':
            return <IconTarget size={16} />;
        case 'COMPETITOR_ADDED':
            return <IconCirclePlus size={16} />;
        case 'COMPETITOR_REMOVED':
            return <IconCircleMinus size={16} />;
        case 'PRICE_ENTRY':
            return <IconEdit size={16} />;
        case 'PROOF_UPLOADED':
            return <IconFile size={16} />;
        case 'SURVEY_SUBMITTED_TO_DM':
            return <IconUserCheck size={16} />;
        case 'SURVEY_COMPLETED':
            return <IconCheck size={16} />;
        case 'SURVEY_PROGRESS_SAVED':
            return <IconDeviceFloppy size={16} />;
        case 'CHAMPION_CHANGED':
            return <IconUserStar size={16} />;
        case 'SURVEY_CANCELLED':
            return <IconX size={16} />;
        default:
            return <IconFile size={16} />;
    }
}

export interface SurveyActivityMetadata {
    sku?: string;
    skus?: string;
    competitorName?: string;
    count?: number;
}

export interface SurveyActivityForDescription {
    type: string;
    description: string;
    metadata?: SurveyActivityMetadata | null;
}

export function getSurveyActivityDescription(
    activity: SurveyActivityForDescription,
): string {
    if (activity.type === 'PRICE_ENTRY' && activity.metadata) {
        const { sku, skus } = activity.metadata;
        if (typeof sku === 'string') {
            return `Updated competitor prices for SKU ${trimLeadingZeros(sku)}`;
        }
        if (typeof skus === 'string') {
            const skuList = skus
                .split(',')
                .map(s => trimLeadingZeros(s.trim()))
                .filter(Boolean);
            if (skuList.length === 0) {
                return activity.description;
            }
            const label = skuList.length > 1 ? 'SKUs' : 'SKU';
            return `Updated competitor prices for ${label} ${skuList.join(
                ', ',
            )}`;
        }
    }
    if (activity.type === 'PROOF_UPLOADED' && activity.metadata) {
        return activity.description;
    }
    if (activity.type === 'SURVEY_PROGRESS_SAVED' && activity.metadata?.skus) {
        const skus = String(activity.metadata.skus);
        const skuList = skus
            .split(',')
            .map(s => trimLeadingZeros(s.trim()))
            .filter(Boolean);
        if (skuList.length === 0) {
            return 'Saved progress';
        }
        const label = skuList.length > 1 ? 'SKUs' : 'SKU';
        return `Saved progress for ${label} ${skuList.join(', ')}`;
    }
    return activity.description;
}
